import { InstrumentationEnum } from '../model/instrumentationUse';
import { Protocol, ProtocolAir, ProtocolQ, ProtocolQTest, ProtocolQTestEssay, ProtocolQTestEssayGroup } from '../model/protocol';
import { TestQType, TestTypeEnum } from '../model/testQType';
import { VariableTypeEnum, VariableUnitEnum } from '../model/variable';

import { ArrayUtils } from './arrayUtils';
import { QualificationUtils } from './qualificationUtils';
import { StringUtils } from './stringUtils';
import { TranslateService } from '@ngx-translate/core';
import { FieldEnum } from './fieldUtils';
import { EssayTypeEnum, EssayUtils } from './essayUtils';
import { TranslationService } from '../services/translation.service';
import { EquipmentType, EquipmentTypeQualificationEnum } from '../model/equipmentType';
import { Field } from '../model/field';
import { EquipmentQualificates } from 'src/app/model/equipment';
import { DateTime, Duration } from 'luxon';
import { ExecutionQTestEssayGroup } from '../model/execution';
import { AirUtils } from './airUtils';

export class ProtocolUtils {

    public static BREAK: string = '<p><br></p>';
    public static IMAGE_STRATEGY = ''

    public static getNameForProtocol(protocol: Protocol, translate: TranslateService): string {
        const documentCode = protocol.documentCode;
        const versionName = 'v.' + protocol.currentVersion.toString().padStart(2, '0');
        const clientName = protocol.nameClient;
        let equipment = null;
        let serialNum = null;
        let internalId = null;

        if (!ArrayUtils.isEmpty(protocol.equipments)) {
            const eq = protocol.equipments[0];

            equipment = eq.equipment;
            if (StringUtils.isNotEmpty(eq.serialNum)) {
                serialNum = '(' + eq.serialNum + ')';
            }

            if (StringUtils.isNotEmpty(eq.internalId)) {
                internalId = '(' + eq.internalId + ')';
            }
        }

        const name = [documentCode, versionName, translate.instant('protocolEdit.files.protocol.documentType'), clientName, equipment,
            serialNum, internalId].filter((s: string) => StringUtils.isNotEmpty(s)).join(' ');

        const res: string = translate.instant('protocolEdit.files.protocol', { name }) as string;

        return res;
    }

    public static getNameForProtocolAir(protocol: ProtocolAir, translate: TranslateService): string {
        const documentCode = protocol.documentCode;
        const versionName = 'v.' + protocol.currentVersion.toString().padStart(2, '0');
        const clientName = protocol.nameClient;

        let areas = null;
        let equipments = null;

        if (ArrayUtils.isNotEmpty(protocol?.areas)) {
            areas = protocol.areas.map(a => a.name).join(', ');
            if (protocol?.areas?.length === 1) {
                const area = protocol?.areas[0];

                if (area.equipments?.length === 1) {
                    equipments = AirUtils.showEquipment(area.equipments[0]);
                } else if (area.rooms?.length === 1) {
                    equipments = area.rooms[0].name;
                } else if (area.compressedGases?.length === 1) {
                    equipments = area.compressedGases[0].code;
                }
            }
        }
        const name = [documentCode, versionName, translate.instant('protocolEdit.files.protocol.documentType'), clientName, areas, StringUtils.isNotEmpty(equipments) ? " (" + equipments + ") " : ""]
            .filter((s: string) => StringUtils.isNotEmpty(s)).join(' ');

        const res: string = translate.instant('protocolEdit.files.protocol', { name }) as string;

        return res;
    }

    public static getNameTestQualificates(protTest: ProtocolQTest,  translate: TranslateService): string {
        let resVariablesTempHumCo = ''
        const resVariables = this.getVariablesFromEssayQualificationTempHumCoTitle(protTest, translate);

        let essay = '';
        if (protTest.protocolQTestEssayGroup?.length === 1) {
            essay = translate.instant('test.essay.singular');
        } else {
            essay = translate.instant('test.essay.plural');
        }

        let openDoorCutElectric = '';
        let isOpenDoor = false;
        let isCutElectric = false; 
        for (let index = 0; index < protTest.protocolQTestEssayGroup?.length; index++) {
            const group = protTest.protocolQTestEssayGroup[index];
            if (!isOpenDoor) {
                isOpenDoor = this.existsOpenDoor(group);
            }
            if (!isCutElectric) {
                isCutElectric = this.existsCutElectric(group);
            }
        }

        if (isOpenDoor && isCutElectric) {
            openDoorCutElectric = `, ${translate.instant('testQualification.openDoor')} ${translate.instant('test.essay.y')} ${translate.instant('testQualification.cutElectric')}`;
        } else if (isOpenDoor && !isCutElectric) {
            openDoorCutElectric = ` ${translate.instant('test.essay.y')} ${translate.instant('testQualification.openDoor')}`;
        } else if (!isOpenDoor && isCutElectric) {
            openDoorCutElectric = ` ${translate.instant('test.essay.y')} ${translate.instant('testQualification.cutElectric')}`;
        } else {
            openDoorCutElectric = '';
        }

        if (protTest.idTestType === TestTypeEnum.TEST_TEMPERATURE_DISTRIBUTION_MOISTURE_CO2_WITH_LOAD_OPENING_DOORS) {
            resVariablesTempHumCo = translate.instant('testQualification.tests_temperature_distribution_load', {essay: essay, variableHumCo: resVariables, openDoorCutElectric: openDoorCutElectric});
        } else if (protTest.idTestType === TestTypeEnum.RELATIVE_MOISTURE_TEMPERATURE_DISTRIBUTION_TEST_CO2_IN_VACUUM) {
            resVariablesTempHumCo = translate.instant('testQualification.tests_temperature_distribution_vacuum', {essay: essay, variableHumCo: resVariables, openDoorCutElectric: openDoorCutElectric});
        } else if (protTest.idTestType === TestTypeEnum.TEMPERATURE_PENETRATION_TEST_UNDER_LOAD_TEMPERATURE) {
            resVariablesTempHumCo = translate.instant('testQualification.tests_temperature_loadPenetration', {essay: essay, variableHumCo: resVariables, openDoorCutElectric: openDoorCutElectric});
        } else if (protTest.idTestType === TestTypeEnum.TEST_BOWIE_DICK) {
            resVariablesTempHumCo = translate.instant('testQualification.test_bowie_dick');
        }  else if (protTest.idTestType === TestTypeEnum.TEST_TIGHTNESS_TEST_VACUUM_TEST_LEAK_TEST) {
            resVariablesTempHumCo = translate.instant('testQualification.test_tightness_test_vacuum_test_leak_test');
        }
        return resVariablesTempHumCo;
    }

    public static getNameForProtocolQualificates(protocol: ProtocolQ, translate: TranslateService): string {
        const documentCode = protocol.documentCode;
        const versionName = 'v.' + protocol.currentVersion.toString().padStart(2, '0');
        const clientName = protocol.nameClient;
        let equipment = null;
        let serialNum = null;
        let internalId = null;

        if (!ArrayUtils.isEmpty(protocol.equipments)) {
            const eq = protocol.equipments[0];

            equipment = eq.equipment;
            if (StringUtils.isNotEmpty(eq.serialNumber)) {
                serialNum = '(' + eq.serialNumber + ')';
            }

            if (StringUtils.isNotEmpty(eq.internalId)) {
                internalId = '(' + eq.internalId + ')';
            }
        }

        const name = [documentCode, versionName, translate.instant('protocolEdit.files.protocol.documentType'), clientName, equipment,
            serialNum, internalId].filter((s: string) => StringUtils.isNotEmpty(s)).join(' ');

        const res: string = translate.instant('protocolEdit.files.protocol', { name }) as string;

        return res;
    }

    public static getObjectiveTestQualificates(protocol: ProtocolQ, protTest: ProtocolQTest,  translate: TranslateService, objetive: string): string {
        let res = '';
        let resVariables;
        const equipmentDela = this.getEquipmentType(protocol, translate, true, false);
        const equipmentLa = this.getEquipmentType(protocol, translate, false, false);

        if (protTest != null && protTest.protocolQTestEssayGroup && (protTest.idTestType != TestTypeEnum.TEST_BOWIE_DICK && protTest.idTestType != TestTypeEnum.TEST_TIGHTNESS_TEST_VACUUM_TEST_LEAK_TEST)) {
            resVariables = this.getVariablesFromEssayQualificationJoin(protTest, translate, false);
            for (let index = 0; index < protTest.protocolQTestEssayGroup.length; index++) {
                const group = protTest.protocolQTestEssayGroup[index];
            }

            const essayText = this.getTotalEssayTextWithCount(protTest.protocolQTestEssayGroup, translate);
            let nameEssay = '';
            if (protTest.idTestType === TestTypeEnum.TEMPERATURE_PENETRATION_TEST_UNDER_LOAD_TEMPERATURE) {
                nameEssay = translate.instant('essay.name.variable.loadPenetration', {essay: essayText, variable: resVariables});
            } else if (protTest.idTestType === TestTypeEnum.TEST_TEMPERATURE_DISTRIBUTION_MOISTURE_CO2_WITH_LOAD_OPENING_DOORS){
                nameEssay = translate.instant('essay.name.variable.distributionLoad', {essay: essayText, variable: resVariables});
            } else {
                nameEssay = translate.instant('essay.name.variable.distributionVaccum', {essay: essayText, variable: resVariables});
            }
            res = `<span>${translate.instant('test.vacuumDistribution.objetive.firstSubtitle.text', {equipment: equipmentDela, variable: resVariables, client: protocol.nameClient, variableNameEssay: nameEssay})}</span>`;
            res += `<p><br></p>`;
            res += `<span>${translate.instant('test.vacuumDistribution.objetive.firstSubtitle.verification', {equipment: equipmentDela, variable: resVariables})}</span>`;
            if (this.isCentrifuges(protocol)) {
                res += `<span>${translate.instant('test.vacuumDistribution.objetive.firstSubtitle.centrifuges', {client: protocol.nameClient})}</span>`;
                res += `<p><br></p>`;
            }
        } else if (protTest.idTestType === TestTypeEnum.TEST_TIGHTNESS_TEST_VACUUM_TEST_LEAK_TEST) {
            res = `<span>${translate.instant('test.tightness.objetive.firstSubtitle')}</span>`;
            res += `<p><br></p>`;
            res += `<span>${translate.instant('test.tightness.objetive.secondSubtitle')}</span>`;
        } else if (protTest.idTestType === TestTypeEnum.TEST_BOWIE_DICK) {
            res += `<span>${translate.instant('test.bowieDick.objetive.firstSubtitle')}</span>`;
            res += `<p><br></p>`;
            res += `<span>${translate.instant('test.bowieDick.objetive.secondSubtitle')}</span>`;
        }else {
            res = this.remplaceVariableInText(protocol, objetive, translate);
        }

        return res;
    }

    public static getBackgroundQualificates(protocol: ProtocolQ, clientName: string, translate: TranslateService) {
        let qualified = '';
        let equipmentWithElLa = this.getEquipmentType(protocol, translate, false, true);
        let equipmentLocation = this.getEquipmentLocation(protocol);
        if (!equipmentWithElLa.endsWith('a')) {
            qualified =  translate.instant('protocol.background.qualified.masc');
        } else {
            qualified = translate.instant('protocol.background.qualified.fem');
        }
        return `<span>${translate.instant('protocolEdit.background', {client: clientName, equipment: equipmentWithElLa, location: equipmentLocation, qualified: qualified})}</span>`;
    }

    public static getTargetQualificates(protocol: ProtocolQ, clientName: string, translate: TranslateService) {
        let installed = '';
        let equipmentWithElLa = this.getEquipmentType(protocol, translate, false, false);
        const qualificationText =  StringUtils.concatenateText(protocol.testPerformed?.map(e => translate.instant('testQualification.variable.description.' + e)), translate);
        const qualification = protocol.testPerformed?.map(e => translate.instant('testQualification.variable.' + e)).join('/')
        if (!equipmentWithElLa.endsWith('a')) {
            installed =  translate.instant('protocol.target.qualified.masc');
        } else {
            installed = translate.instant('protocol.target.qualified.fem');
        }
        return `<span>${translate.instant('protocolEdit.target', {client: clientName, equipment: equipmentWithElLa, qualificationText: qualificationText, qualification: qualification, installed: installed})}</span>`;
    }

    public static getScopeQualificates(protocol: ProtocolQ, clientName: string, directionClient: string, translate: TranslateService): string {
        let qualified = '';
        let equipmentWithElLa = this.getEquipmentNameComplete(protocol, translate, false, false);
        let equipmentLocation = this.getEquipmentLocation(protocol);
        let equipmenMaker = this.getEquipmenMaker(protocol);
        let equipmenModel = this.getEquipmentModel(protocol);
        let equipmenSerialNumber = this.getEquipmenSerialNumber(protocol);
        let equipmenInternalId = this.getEquipmentInternalId(protocol);

        const qualificationText = StringUtils.concatenateText(protocol.testPerformed?.map(e => translate.instant('testQualification.variable.description.' + e)), translate);
        const qualification = protocol.testPerformed?.map(e => translate.instant('testQualification.variable.' + e)).join('/')
        if (!equipmentWithElLa.endsWith('a')) {
            qualified =  translate.instant('protocol.background.qualified.masc');
        } else {
            qualified = translate.instant('protocol.background.qualified.fem');
        }

        let serieText = '';
        if (equipmenSerialNumber && StringUtils.isNotEmpty(equipmenSerialNumber)) {
            serieText = ' ' +translate.instant('protocolEdit.scope.nserie', {nSerie: equipmenSerialNumber});
        }
        let equipmenMakerText = '';
        if (equipmenMaker && StringUtils.isNotEmpty(equipmenMaker)) {
            equipmenMakerText = ' ' +translate.instant('protocolEdit.scope.maker', {maker: equipmenMaker});
        }
        let equipmenModelText = '';
        if (equipmenModel && StringUtils.isNotEmpty(equipmenModel)) {
            equipmenModelText = ' ' +translate.instant('protocolEdit.scope.model', {modelCode: equipmenModel});
        }
        let equipmenInternalIdText = '';
        if (equipmenInternalId && StringUtils.isNotEmpty(equipmenInternalId)) {
            equipmenInternalIdText =' ' + translate.instant('protocolEdit.scope.identificative', {idInternal: equipmenInternalId});
        }
        return `<span>${translate.instant('protocolEdit.scope', {equipment: equipmentWithElLa, location: equipmentLocation, qualificationText, qualification: qualification, manufacturer: equipmenMakerText, modelCode: equipmenModelText, nSerie: serieText, idInternal: equipmenInternalIdText, client: clientName, directionClient: directionClient})}</span>`;
    }

    public static getMethodologyTestQualificates(protocol: ProtocolQ, protTest: ProtocolQTest, translate: TranslateService, methodology: string): string {
        let res = '';
        let isProbePressure = false;
        let isProbeCo2 = false;
        let probeNumberTempHum = 0;
        let probeNumberCo2 = 0;
        let numberProbePressure = 0;
        let resDifferentProbes = '';
        let resDifferentFrequency = '';
        let resDifferentCycle = '';
        let cycleText = '';
        let essayText = '';
        let frequencyOpen = '';
        let frequencyCut = ''
        let numberEssay = 0;
        let frequency = this.getFrecuency(protTest, translate);

        let differencySensor = this.differenceInSensory(protTest);
        let differencyFrequency = this.differenceInFrequency(protTest);
        let differencyCycle = this.differenceInCycle(protTest);

        let equipmentWithDelDeLa = this.getEquipmentType(protocol, translate, true, false);
        let equipmentWithElLa = this.getEquipmentType(protocol, translate, false, false);
        let isOpenDoor = false;
        let isCutElectric = false;
        let isOuterProbe = false;
        let isLethality = false;
        let variableSensor = '';
        let hasBioindicador = false;
        let resVariableTempHum  = this.getVariablesFromEssayQualificationJoin(protTest, translate, true)?.toLowerCase();
        let resVariables = this.getVariablesFromEssayQualificationJoin(protTest, translate, false);
        let resVariablesWithoutLeth = this.getVariablesJoinWithoutLethality(protTest, translate, false)?.toLowerCase();
        if (protTest != null && protTest.protocolQTestEssayGroup && (protTest.idTestType != TestTypeEnum.TEST_BOWIE_DICK && protTest.idTestType != TestTypeEnum.TEST_TIGHTNESS_TEST_VACUUM_TEST_LEAK_TEST)) {
            let totalCycle = this.getNumCycleByGroup(protTest.protocolQTestEssayGroup, translate);
            let totalEssay =  this.getTotalEssay(protTest.protocolQTestEssayGroup);
            let totalEssayText = this.getTotalEssayText(protTest.protocolQTestEssayGroup, translate);
            isLethality = this.getLethalityAccumulated(protTest.protocolQTestEssayGroup);
            hasBioindicador = this.hasBioindicador(protTest.protocolQTestEssayGroup);
            for (let index = 0; index < protTest.protocolQTestEssayGroup.length; index++) {
                const group = protTest.protocolQTestEssayGroup[index];
                probeNumberTempHum = this.getNumberSensorTemperatureHumd(group);
                probeNumberCo2 = this.getNumberSensorCo2(group);
                numberProbePressure = this.getNumberSensorPressure(group);
                numberEssay = this.getNumberSensor(group);
                essayText = this.getEssayType(group, translate);
                if (!isProbePressure) {
                    isProbePressure = this.existsPressureProbe(group);
                }
                if (!isProbeCo2) {
                    isProbeCo2 = this.existsPressureCo2(group);
                }
                if (!isOpenDoor) {
                    isOpenDoor = this.existsOpenDoor(group);
                }
                if (!isCutElectric) {
                    isCutElectric = this.existsCutElectric(group);
                }

                if (group.idType === EssayTypeEnum.ENSAYO_APERTURA_DE_PUERTA) {
                    frequencyOpen = this.frequencyEssay(group, translate);
                }

                if (group.idType === EssayTypeEnum.ENSAYO_CORTE_ELECTRICO) {
                    frequencyCut = this.frequencyEssay(group, translate);
                }

                if (!isOuterProbe) {
                    isOuterProbe = this.existsOutsideProbe(group);
                    variableSensor = this.getVariableOutsideProbe(group, translate);
                }
            }

            res += `<p><strong><u><i>${translate.instant('test.loadDistribution.methodology.firstSubtitle.title')}</i></u></strong></p>`;
            res += `<p></p>`;

            let firstSubtitlePressure = '';
            let firstSubtitleCo2 = '';

            if (isProbePressure) {
                firstSubtitlePressure += `${translate.instant('test.loadDistribution.methodology.firstSubtitle.pressureProbe.text')}`;
            }
            if (isProbeCo2) {
                firstSubtitleCo2 += `${translate.instant('test.loadDistribution.methodology.firstSubtitle.co2Probe.text')}`;
            }

            let probe = '';
            if (this.existsInstToDefine(protTest)) {
                probe = ` ${translate.instant('test.loadDistribution.methodology.firstSubtitle.probe.toDefine', {variableTempHum: resVariableTempHum})}`;
            } else if (this.existsInstRegisterData(protTest)) {
                probe = ` ${translate.instant('test.loadDistribution.methodology.firstSubtitle.probe.pt100')}`;
            } else if (this.existsInstValidator(protTest)) {
                probe = ` ${translate.instant('test.loadDistribution.methodology.firstSubtitle.probe.termopar')}`;
            } else if (this.existsInstDataLogger(protTest)) {
                probe = ` ${translate.instant('test.loadDistribution.methodology.firstSubtitle.probe.datalogger', {variableTempHum: resVariableTempHum})}`
            }
            res += `<p>${translate.instant('test.loadDistribution.methodology.firstSubtitle.text', {variableTempHum: resVariableTempHum, textPressure: firstSubtitlePressure, textCo2: firstSubtitleCo2, probe: probe})}</p>`;
            res += `<p></p>`;

            if (this.existsInstRegisterData(protTest)) {
                res += `<ul><li><span><strong><u>${translate.instant('test.loadDistribution.methodology.firstSubtitle.list.tempProbe.title')}</u></strong></span></li></ul><span>${translate.instant('test.vacuumDistribution.methodology.firstSubtitle.list.tempProbe.description')}</span>`;
                res += `<p></p>`;
            }

            if (this.existsInstValidator(protTest)) {
                res += `<ul><li><span><strong><u>${translate.instant('test.loadDistribution.methodology.firstSubtitle.list.tempPar.title')}</u></strong></span></li></ul><span>${translate.instant('test.vacuumDistribution.methodology.firstSubtitle.list.tempPar.description')}</span>`;
                res += `<p></p>`;
            }

            if (this.existsInstDataLogger(protTest)) {
                res += `<ul><li><span><strong><u>${translate.instant('test.loadDistribution.methodology.firstSubtitle.list.dataloogger.title', {variable: resVariableTempHum})}</u></strong></span></li></ul><span>${translate.instant('test.vacuumDistribution.methodology.firstSubtitle.list.dataloogger.description', {variable: resVariableTempHum})}</span>`;
                res += `<p></p>`;
            }

            if (this.existsInstToDefine(protTest)) {
                res += `<ul><li><span><strong><u>${translate.instant('test.loadDistribution.methodology.firstSubtitle.list.tempProbe.title')}</u></strong></span><br></li></ul><span>${translate.instant('test.vacuumDistribution.methodology.firstSubtitle.list.tempProbe.description')}</span>`;
                res += `<p></p>`;
                res += `<ul><li><span><strong><u>${translate.instant('test.loadDistribution.methodology.firstSubtitle.list.tempPar.title')}</u></strong></span><br></li></ul><span>${translate.instant('test.vacuumDistribution.methodology.firstSubtitle.list.tempPar.description')}</span>`;
                res += `<p></p>`;
                res += `<ul><li><span><strong><u>${translate.instant('test.loadDistribution.methodology.firstSubtitle.list.dataloogger.title', {variable: resVariableTempHum})}</u></strong></span><br></li></ul><span>${translate.instant('test.vacuumDistribution.methodology.firstSubtitle.list.dataloogger.description', {variable: resVariableTempHum})}</span>`;
                res += `<p></p>`;
            }

            if (isProbePressure) {
                res += `<ul><li><span><strong><u>${translate.instant('test.loadDistribution.methodology.firstSubtitle.list.pressureProbe.title')}</u></strong></span></li></ul><span>${translate.instant('test.vacuumDistribution.methodology.firstSubtitle.list.pressureProbe.description')}</span>`;
                res += `<p></p>`;
            }

            if (isProbeCo2) {
                res += `<ul><li><span><strong><u>${translate.instant('test.loadDistribution.methodology.firstSubtitle.list.co2Probe.title')}</u></strong></span></li></ul><span>${translate.instant('test.vacuumDistribution.methodology.firstSubtitle.list.co2Probe.description')}</span>`;
                res += `<p></p>`;
            }

            res += `<p><strong><u>${this.getNameTestQualificates(protTest, translate)}</u></strong></p>`;

            if ((protTest.idTestType === TestTypeEnum.RELATIVE_MOISTURE_TEMPERATURE_DISTRIBUTION_TEST_CO2_IN_VACUUM) || (protTest.idTestType === TestTypeEnum.TEST_TEMPERATURE_DISTRIBUTION_MOISTURE_CO2_WITH_LOAD_OPENING_DOORS)) {
                resDifferentProbes += `<p>${translate.instant('test.vacuumDistribution.methodology.secondSubtitle.differenceSensor.text', {trial: totalEssayText, variable: resVariables, equipment: equipmentWithDelDeLa })}</p>`;
                resDifferentProbes += `<p></p>`;
            } else if (TestTypeEnum.TEMPERATURE_PENETRATION_TEST_UNDER_LOAD_TEMPERATURE) {
                resDifferentProbes += `<p>${translate.instant('test.loadPenetration.methodology.secondSubtitle.differenceSensor.text', {trial: totalEssayText, variable: resVariables, equipment: equipmentWithDelDeLa })}</p>`;
                resDifferentProbes += `<p></p>`;
            }

            let textComplemetEssayCycle = translate.instant('test.loadDistribution.methodology.secondSubtitle.cycle.essay.plural');
            let textComplementEssay = translate.instant('test.loadDistribution.methodology.secondSubtitle.frequency.text.plural');
            if (totalEssay == 1) {
                textComplementEssay = translate.instant('test.loadDistribution.methodology.secondSubtitle.frequency.text.singular');
                textComplemetEssayCycle = translate.instant('test.loadDistribution.methodology.secondSubtitle.cycle.essay.singular', {essayName: essayText});
            }
            resDifferentFrequency += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.frequency.text', {variable: resVariables, complementTextEssay: textComplementEssay})}</p>`;
            resDifferentCycle += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.cycle.text', {totalCycleText: totalCycle, titleEssay: textComplemetEssayCycle})}</p>`;

            for (let index = 0; index < protTest.protocolQTestEssayGroup.length; index++) {
                const group = protTest.protocolQTestEssayGroup[index];
                
                if (differencySensor || totalEssay > 1) {
                    resDifferentProbes += this.getDifferenceSensor(group, translate);
                }
                if (differencyFrequency || totalEssay > 1) {
                    if (index === 0) {
                        resDifferentFrequency += `<p></p>`;
                    }
                    resDifferentFrequency += this.getDifferenceFrequency(group, translate);
                }
                if (differencyCycle || totalEssay > 1) {
                    if (index === 0) {
                        resDifferentCycle += `<p></p>`;
                    }
                    resDifferentCycle += this.getDifferenceCycle(group, translate);
                }
            }

            if (differencySensor) {
                res += resDifferentProbes;
                res += `<p></p>`;
            } else {
                let textCo2 = '';
                if (isProbeCo2) {
                    if (probeNumberCo2 === 1) {
                        textCo2 = `${translate.instant('test.loadDistribution.methodology.secondSubtitle.text.co2.singular', {probeNumberCo2: probeNumberCo2})}`;
                    } else {
                        textCo2 = `${translate.instant('test.loadDistribution.methodology.secondSubtitle.text.co2.plural', {probeNumberCo2: probeNumberCo2})}`;
                    }
                }

                res += `<p>${translate.instant('test.vacuumDistribution.methodology.secondSubtitle.text', {trial: totalEssayText, variable: resVariables, probeNumberTempHum: probeNumberTempHum, variableTempHum: resVariableTempHum, probeCo2Text: textCo2, equipment: equipmentWithElLa})}</p>`;
                res += `<p><br></p>`;
            }

            if (hasBioindicador) {
                res += `<p>${translate.instant('essay.sensor.biondicador.text')}</p>`;
                res += `<p><br></p>`; 
            }
            
            if ((protTest.idTestType === TestTypeEnum.TEST_TEMPERATURE_DISTRIBUTION_MOISTURE_CO2_WITH_LOAD_OPENING_DOORS) || (protTest.idTestType === TestTypeEnum.TEMPERATURE_PENETRATION_TEST_UNDER_LOAD_TEMPERATURE)) {
                res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.load', {phaseEssay: totalEssayText})}</p>`;
                res += `<p><br></p>`;
                res += this.getDescriptionLoadWithTitle(protTest.protocolQTestEssayGroup, translate);
                res += `<p></p>`;
            }

            if (this.isTankN2(protocol)) {
                res += `<p>${translate.instant('test.methodology.secondSubtitle.n2.text', {equipment: equipmentWithDelDeLa})}</p>`;
                res += `<p><br></p>`;
            }

            let startEssay  = '';
            if (totalEssay > 1) {
                startEssay =  translate.instant('test.verification.start.essay.plural', {phaseEssay: totalEssayText});
            } else {
                startEssay =  translate.instant('test.verification.start.essay.singular', {phaseEssay: totalEssayText});
            }

            res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.closedAndStart', {equipment: equipmentWithElLa, startEssay})}</p>`;
            res += `<p><br></p>`;

            if (differencyFrequency) {
                res += resDifferentFrequency;
                res += `<p></p>`;
            } else {
                res +=  `<p>${translate.instant('test.vacuumDistribution.methodology.secondSubtitle.dataRecording', {variable: resVariables, timeVariable: frequency } )}</p>`;
                res += `<p><br></p>`;
            }

            res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.dataCompilation', {variable: resVariablesWithoutLeth})}</p>`;
            res += `<p></p>`;

            if (isLethality) {
               if (this.isWasherType(protocol)) {
                res += `<p>${translate.instant('test.washer.methodology.cumulativeLethality.text')}</p>`;
                res += `<p></p>`;
                res += `<p class="ql-align-center">${translate.instant('test.washer.methodology.cumulativeLethality.formula')}</p>`;
                res += `<p></p>`;
                res += `<p>${translate.instant('test.washer.methodology.cumulativeLethality.where')}</p>`;
                res += `<p>${translate.instant('test.washer.methodology.cumulativeLethality.t')}</p>`;
                res += `<p>${translate.instant('test.washer.methodology.cumulativeLethality.T')}</p>`;
                res += `<p>${translate.instant('test.washer.methodology.cumulativeLethality.Z')}</p>`;
                res += `<p>${translate.instant('test.washer.methodology.cumulativeLethality.L')}</p>`;
               } else {
                res += `<p>${translate.instant('test.loadDistribution.methodology.cumulativeLethality.text')}</p>`;
                res += `<p></p>`;
                res += `<p class="ql-align-center">${translate.instant('test.loadDistribution.methodology.cumulativeLethality.formula')}</p>`;
                res += `<p></p>`;
                res += `<p>${translate.instant('test.loadDistribution.methodology.cumulativeLethality.where')}</p>`;
                res += `<p>${translate.instant('test.loadDistribution.methodology.cumulativeLethality.t')}</p>`;
                res += `<p>${translate.instant('test.loadDistribution.methodology.cumulativeLethality.T')}</p>`;
                res += `<p>${translate.instant('test.loadDistribution.methodology.cumulativeLethality.Z')}</p>`;
                res += `<p>${translate.instant('test.loadDistribution.methodology.cumulativeLethality.L')}</p>`;
               }
               res += `<p><br></p>`;
            }

            res += resDifferentCycle;
            res += `<p></p>`;

            if (isOuterProbe) {
                res += `<p>${translate.instant('test.methodology.probes.outside.text', {variable: variableSensor, equipment: equipmentWithElLa})}</p>`;
                res += `<p><br></p>`;
            }

            res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.endCycle', {cycles: totalEssayText, equipment: equipmentWithElLa})}</p>`;
            res += `<p><br></p>`;

            if (isOpenDoor || isCutElectric) {
                let totalOpenDoor = this.getTotalOpenDoorCutElectric(protTest.protocolQTestEssayGroup, translate);

                let openDoorText = this.getTotalOpenDoorText(protTest.protocolQTestEssayGroup, equipmentWithElLa, translate);
                let cutElectricText = this.getTotalCutElectricText(protTest.protocolQTestEssayGroup, translate);
                let openCutText = '';

                if ((openDoorText) && (cutElectricText)) {
                    openCutText = `${openDoorText} ${translate.instant('test.essay.y')} ${cutElectricText}`;

                } else if( (openDoorText) && (!cutElectricText)) {
                    openCutText = openDoorText;
                } else if ((!openDoorText) && (cutElectricText)) {
                    openCutText = cutElectricText;
                }

                res += `<p>${translate.instant('test.methodology.time.thirdSubtitle.text', {countOpenCutElectric: openCutText, variable: resVariables, equipment: equipmentWithElLa, openCutElectric: totalOpenDoor})}</p>`;
                res += `<p><br></p>`;
            }
            // Equipments
            if (this.isLiofilizador(protocol)) {
                res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.lyophilizers')}</p>`;
                res += `<p><br></p>`;
            }
            if (this.isCentrifuges(protocol)) {
                res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.centrifuges')}</p>`;
                res += `<p><br></p>`;
            }
            if (this.isN2Liquid(protocol)) {
                res += `<p style="color: red;">${translate.instant('test.loadDistribution.methodology.secondSubtitle.n2Liquid')}</p>`;
                res += `<p><br></p>`;
            }

            if (this.isTankN2(protocol)) {
                res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.n2Gas')}</p>`;
                res += `<p><br></p>`;
            }

            let textPressure ='';
            /*if (isProbePressure) {
                textPressure =  `<span>${translate.instant('test.loadDistribution.methodology.secondSubtitle.verificationMatrix.pressure')}</span>`;
            }*/
            res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.verificationMatrix', {equipment: equipmentWithDelDeLa, presionProbe: textPressure})}</p>`;
        } else if (protTest.idTestType === TestTypeEnum.TEST_TIGHTNESS_TEST_VACUUM_TEST_LEAK_TEST) {
            res += `<p style="color:red;">${translate.instant('test.tightness.methodology.text')}</p>`;
            res += `<p><br></p>`;
            res += `<p><strong><u><i>${translate.instant('test.tightness.methodology.firstSubtitle.title')}</i></u></strong>${this.BREAK}</p>`;
            res += `<ul><li><span><strong><u>${translate.instant('test.loadDistribution.methodology.firstSubtitle.list.pressureProbe.title')}</u></strong></span></li></ul><br><span>${translate.instant('test.vacuumDistribution.methodology.firstSubtitle.list.pressureProbe.description')}${this.BREAK}</span>`;
            res += `<p style="color:red;">${translate.instant('test.tightness.methodology.secondSubtitle.text')}</p>`;
        } else if (protTest.idTestType === TestTypeEnum.TEST_BOWIE_DICK) {
            res += `<p>${translate.instant('test.bowieDick.methodology.firstSubtitle')}</p>`;
        } else {
            res += this.remplaceVariableInText(protocol, methodology, translate);
        }
        return res;
    }

    public static getCriteriaTestQualificates(protocol: ProtocolQ, protTest: ProtocolQTest, translate: TranslateService, criteria: string): string {
        let res = '';
        let equipmentWithDelDeLa = this.getEquipmentType(protocol, translate, true, false);
        let equipmentWithElLa = this.getEquipmentType(protocol, translate, false, false);
        if (protTest != null && protTest.protocolQTestEssayGroup && (protTest.idTestType != TestTypeEnum.TEST_BOWIE_DICK && protTest.idTestType != TestTypeEnum.TEST_TIGHTNESS_TEST_VACUUM_TEST_LEAK_TEST)) {
            let resCriteria ='';
            let resEssay = '';
            let isProbeCo2 = false;
            let isOpenDoor = false;
            let isCutElectric = false;
            let isOutside = false;
            let frequencyOpen = '';
            let frequencyCut = '';
            let cycle = 0;
            let allowedEssay = [];

            let totalEssay = this.getTotalEssay(protTest.protocolQTestEssayGroup);
            
            let resVariables = this.getVariablesFromEssayQualificationJoin(protTest, translate, false);
            for (let index = 0; index < protTest.protocolQTestEssayGroup.length; index++) {
                
                resEssay += this.BREAK;
                const group = protTest.protocolQTestEssayGroup[index];
                const hasBios = this.hasBioindicadorIndivitual(group);

                if (!isProbeCo2) {
                    isProbeCo2 = this.existsPressureCo2(group);
                }
                cycle = this.getCycleFields(group);
                if (totalEssay > 1 && !allowedEssay.includes(group.uuid)) {
                    let essay = (translate.instant('essayType.' + group.translationType) as string);
                    if (!essay.includes(translate.instant('test.essay.singular') as string)) {
                        essay = `${translate.instant('test.essay.singular')} ${essay.toLowerCase()}`;

                        if (StringUtils.isNotEmpty(group.title)) {
                            essay += ` (${group.title})`;
                        }
                    }
                    
                    resEssay += `<p>- ${essay}:</p>`;
                }
                const resCriterias = this.getCriteriaFromEssayComplete(group, translate, cycle);
                resCriteria = '';

                for (let indexC = 0; indexC < resCriterias.length; indexC++) {
                    const element= resCriterias[indexC];
                    let elements = [...element];
                    for (let indexCriteria = 0; indexCriteria < elements.length; indexCriteria++) {
                        const elementLi = elements[indexCriteria];
                        resCriteria += `<li>${elementLi}</li>`;
                    }
                }

                if (hasBios) {
                    resCriteria += `<li>${translate.instant('essay.criteria.bio.noExists')}</li>`;
                    resCriteria += `<li>${translate.instant('essay.criteria.bio.existsPositive')}</li>`;
                }

                resEssay += `<ul>${resCriteria}</ul>`;

                if (group.idType === EssayTypeEnum.ENSAYO_APERTURA_DE_PUERTA) {
                    frequencyOpen = this.frequencyEssay(group, translate);
                }

                if (group.idType === EssayTypeEnum.ENSAYO_CORTE_ELECTRICO) {
                    frequencyCut = this.frequencyEssay(group, translate);
                }

                if (!isOpenDoor) {
                    isOpenDoor = this.existsOpenDoor(group);
                }
                if (!isCutElectric) {
                    isCutElectric = this.existsCutElectric(group);
                }

                if (!isOutside) {
                    isOutside = this.existsOutsideProbe(group);
                }
                
            }

            res += `<p>${translate.instant('test.vacuumDistribution.criteriaAcceptace.firstSubtitle.text')}</p>`;
            res += resEssay;
            res += `<p><br></p>`;

            let resExterior = '';
            if (isOutside) {
                resExterior = translate.instant('test.loadDistribution.acceptanceCriteria.secondSubtitle.exterior.text', {variable: resVariables });
            }
            res += `<p>${translate.instant('test.loadDistribution.acceptanceCriteria.secondSubtitle.text', {equipment: equipmentWithDelDeLa, variable: resVariables, exterior: resExterior})}</p>`;
            res += `<p></p>`;
            /*if (isProbeCo2) {
                res += `<p>${translate.instant('test.loadDistribution.acceptanceCriteria.secondSubtitle.cutElectric.text', {equipment: equipmentWithDelDeLa})}</p>`;
                res += `<p></p>`;
            }*/

            if (isOpenDoor || isCutElectric) {

                let openDoorText = this.getTotalOpenDoorText(protTest.protocolQTestEssayGroup, equipmentWithElLa, translate);
                let cutElectricText = this.getTotalCutElectricText(protTest.protocolQTestEssayGroup, translate);

                let openCutText = '';

                if ((openDoorText) && (cutElectricText)) {
                    openCutText = `${openDoorText} ${translate.instant('test.essay.y')} ${cutElectricText}`;

                } else if( (openDoorText) && (!cutElectricText)) {
                    openCutText = openDoorText;
                } else if ((!openDoorText) && (cutElectricText)) {
                    openCutText = cutElectricText;
                }

                res += `<p>${translate.instant('test.criteriaAcceptace.time.thirdSubtitle.text', {equipment: equipmentWithElLa, variable: resVariables, countOpenCutElectric: openCutText})}</p>`;
                res += `<p><br></p>`;
            }

            res += `<p>${translate.instant('test.vacuumDistribution.criteriaAcceptace.thirdSubtitle.text', {client: protocol.nameClient})}</p>`;
        } else if(protTest.idTestType === TestTypeEnum.TEST_TIGHTNESS_TEST_VACUUM_TEST_LEAK_TEST) {
            res += `<p>${translate.instant('test.tightness.methodology.criteria.text', {equipment: equipmentWithElLa})}</p>`;
        } else if (protTest.idTestType === TestTypeEnum.TEST_BOWIE_DICK) {
            res += `<p>${translate.instant('test.bowieDick.criteria.firstSubtitle')}</p>`;
            res += `<p></p>`;
            res += `<p>${translate.instant('test.bowieDick.criteria.secondSubtitle')}</p>`;
            res += `<p></p>`;
            res += `<p>${translate.instant('test.bowieDick.criteria.thirdSubtitle')}</p>`;
        }else {
            res = this.remplaceVariableInText(protocol, criteria, translate);
        }
        return res;
    }

    public static getVariablesFromEssayQualification(essay: ProtocolQTestEssayGroup, translate: TranslateService): string {
		return essay.essayValues.map(i => {
            if (i.idVariable === VariableTypeEnum.CO2) {
                return translate.instant('variable.' + i.translationVariable) as string;
            } else {
                return (translate.instant('variable.' + i.translationVariable) as string).toLowerCase();
            }
        }).join(', ');
	}


    public static getVariablesFromEssayQualificationJoin(essay: ProtocolQTest, translate: TranslateService, isTempHum: boolean): string {
        let translatedValues: string[] = [];
        let variable: number[] = [];
        for (let index = 0; index < essay.protocolQTestEssayGroup?.length; index++) {
            const element = essay.protocolQTestEssayGroup[index];
            element.essayValues
            .filter(ff => !variable.includes(ff.idVariable))
            .filter(i => {
                if (isTempHum) {
                    return i.idVariable === VariableTypeEnum.TEMPERATURE || i.idVariable === VariableTypeEnum.HUMIDITY;
                } else {
                    return i;
                }
            })
            .map(i => {
                variable.push(i.idVariable);
                const keyPrefix = i.idVariable === VariableTypeEnum.CO2 ? 'variableSub.' : 'variable.';
                const translated = translate.instant(keyPrefix + i.translationVariable);
                return translatedValues.push(i.idVariable === VariableTypeEnum.CO2 ? translated : translated.toLowerCase());
            });
        }

        translatedValues = Array.from(new Set(translatedValues));
                
        if (translatedValues.length === 1) {
            return translatedValues[0];
        }
    
        if (translatedValues.length === 2) {
            return translatedValues.join(' y ');
        }
        const lastElement = translatedValues.pop();
        return `${translatedValues.join(', ')} y ${lastElement}`;
    }

    public static getVariablesFromEssayQualificationTempHumCo(test: ProtocolQTest, translate: TranslateService): string {
        let translatedValues: string[] = [];
    
        for (let index = 0; index < test.protocolQTestEssayGroup?.length; index++) {
            const essay = test.protocolQTestEssayGroup[index];

            for (let indexValue = 0; indexValue < essay.essayValues.length; indexValue++) {
                const elementValue =  essay.essayValues[indexValue];

                if (!translatedValues.includes(translate.instant('variable.' + elementValue?.translationVariable))) {
                   if (elementValue.idVariable === VariableTypeEnum.CO2) {
                        translatedValues.push(translate.instant('variableSub.' + elementValue?.translationVariable) as string);
                   } else {
                        translatedValues.push((translate.instant('variable.' + elementValue?.translationVariable) as string).toLowerCase());
                   }
                }
            }
        }
    
        translatedValues = Array.from(new Set(translatedValues));
        
        if (translatedValues.length === 1) {
            return translatedValues[0];
        }
    
        if (translatedValues.length === 2) {
            return translatedValues.join(' y ');
        }
    
        const lastElement = translatedValues.pop();
        return `${translatedValues.join(', ')} y ${lastElement}`;
    }

    public static getVariablesFromEssayQualificationTempHumCoTitle(test: ProtocolQTest, translate: TranslateService): string {
        let translatedValues: string[] = [];
    
        for (let index = 0; index < test.protocolQTestEssayGroup?.length; index++) {
            const essay = test.protocolQTestEssayGroup[index];

            for (let indexValue = 0; indexValue < essay.essayValues.length; indexValue++) {
                const elementValue =  essay.essayValues[indexValue];

                if (!translatedValues.includes(translate.instant('variable.' + elementValue?.translationVariable))) {
                   if (elementValue.idVariable === VariableTypeEnum.CO2) {
                        translatedValues.push(translate.instant('variableSubTitle.' + elementValue?.translationVariable) as string);
                   } else {
                        translatedValues.push((translate.instant('variable.' + elementValue?.translationVariable) as string).toLowerCase());
                   }
                }
            }
        }
    
        translatedValues = Array.from(new Set(translatedValues));
        
        if (translatedValues.length === 1) {
            return translatedValues[0];
        }
    
        if (translatedValues.length === 2) {
            return translatedValues.join(' y ');
        }
    
        const lastElement = translatedValues.pop();
        return `${translatedValues.join(', ')} y ${lastElement}`;
    }

    public static getVariablesEssayByGroupJoin(group: ProtocolQTestEssayGroup, translate: TranslateService, isTempHum: boolean): string {
        let translatedValues: string[] = [];
    
        if (isTempHum) {
            translatedValues = group.essayValues
                .filter(i => i.idVariable === VariableTypeEnum.TEMPERATURE || i.idVariable === VariableTypeEnum.HUMIDITY)
                .map(i => translate.instant('variable.' + i.translationVariable));
        } else {
            translatedValues = group.essayValues
                .map(i => {
                    if (i.idVariable === VariableTypeEnum.CO2) {
                        return translate.instant('variableSub.' + i.translationVariable);
                    } else {
                        return (translate.instant('variable.' + i.translationVariable) as string).toLowerCase();
                    }
                });
        }
    
        translatedValues = Array.from(new Set(translatedValues));
        
        if (translatedValues.length === 1) {
            return translatedValues[0];
        }
    
        if (translatedValues.length === 2) {
            return translatedValues.join(` ${translate.instant('test.essay.y')} `);
        }
        const lastElement = translatedValues.pop();
        return `${translatedValues.join(', ')} ${translate.instant('test.essay.y')} ${lastElement}`;
    }
      
      
    public static existsPressureProbe(essay: ProtocolQTestEssayGroup): boolean {
        return essay.essayValues.some(element => element.idVariable === VariableTypeEnum.PRESSURE);
    }

    public static existsPressureCo2(essay: ProtocolQTestEssayGroup): boolean {
        return essay.essayValues.some(element => element.idVariable === VariableTypeEnum.CO2);
    }

    public static existsTempHumd(essay: ProtocolQTestEssayGroup): boolean {
        return essay.essayValues.some(element => element.idVariable === VariableTypeEnum.TEMPERATURE || element.idVariable === VariableTypeEnum.LETHALITY);
    }

    public static existsInstDataLogger(test: ProtocolQTest): boolean {
        if (!test.protocolQTestInstrumentalUsed) {
            return false;
        }
        return test.protocolQTestInstrumentalUsed.some(e => e.idInstrumentation === InstrumentationEnum.DATA_LOGGER);
    }

    public static existsInstRegisterData(test: ProtocolQTest): boolean {
        if (!test.protocolQTestInstrumentalUsed) {
            return false;
        }
        return test.protocolQTestInstrumentalUsed.some(e => e.idInstrumentation === InstrumentationEnum.REGISTRADOR_DATOS);
    }

    public static existsInstValidator(test: ProtocolQTest): boolean {
        if (!test.protocolQTestInstrumentalUsed) {
            return false;
        }
        return test.protocolQTestInstrumentalUsed.some(e => e.idInstrumentation === InstrumentationEnum.VALIDATOR);
    }

    public static existsInstToDefine(test: ProtocolQTest): boolean {
        if (!test.protocolQTestInstrumentalUsed) {
            return false;
        }
        return test.protocolQTestInstrumentalUsed.some(e => e.idInstrumentation === InstrumentationEnum.A_DEFINIR);
    }

    public static isCentrifuges(protocol: ProtocolQ): boolean {
        return protocol.equipments.some(element => element.idType === EquipmentTypeQualificationEnum.CENTRIFUGE);
    }

    public static isN2Liquid(protocol: ProtocolQ): boolean {
        return false; //protocol.equipments.some(element => element.idType === QualificationUtils.EQUIPMENT_TYPE);
    }

    public static isTankN2(protocol: ProtocolQ): boolean {
        return protocol.equipments.some(element => element.idType === EquipmentTypeQualificationEnum.TANK_N2);
    }

    public static isWarehouses(protocol: ProtocolQ): boolean {
        return protocol.equipments.some(element => element.idType === EquipmentTypeQualificationEnum.WAREHOUSE);
    }

    public static isWasherType(protocol: ProtocolQ): boolean {
        return protocol.equipments.some(element => element.idType === EquipmentTypeQualificationEnum.WASHER_TYPE);
    }


    public static getFrecuency(test: ProtocolQTest, translate: TranslateService): string {
        let frequency = [];
        for (let index = 0; index < test.protocolQTestEssayGroup?.length; index++) {
            const element = test.protocolQTestEssayGroup[index];

            const highestFrequency = element.essayValues?.[0]?.fields?.filter(field => field.idField === FieldEnum.FREQUENCY).map(field => field.value).reduce((max, current) => Math.max(max, current), 0);
            if (highestFrequency) {
               frequency.push(highestFrequency);
            }
        }
    
        if (frequency != null && frequency.every(num => num === frequency[0])) {
            return this.getFreqMinSecs(frequency[0], translate);
        } else {
            return this.getFreqMinSecs(frequency.toString(), translate);
        }
        // Convertir la frecuencia a un formato adecuado
       
    }
    

    public static getNumCycleTextIndividual(essay: ProtocolQTestEssayGroup, translate: TranslateService): string {
        let cycleText = '';        
        const totalCycles = essay.essayValues[0].fields?.find(field => field.idField === FieldEnum.NUM_CYCLES)?.value; 

        if (totalCycles) {
            const res = Number(totalCycles);
            if (res === 1) {
                cycleText = translate.instant('test.essay.cycle.singular', {cycle: totalCycles});
            } else {
                cycleText = translate.instant('test.essay.cycle.plural', {cycle: totalCycles});
            }
        } else {
            cycleText = translate.instant('test.contracted.items.elcycle', {cycle: totalCycles});
        }
        return cycleText;
    }


    public static getNumCycleIndividual(essay: ProtocolQTestEssayGroup): number {
        const res = essay.essayValues[0].fields?.find(field => field.idField === FieldEnum.NUM_CYCLES);
        return res ? Number(res.value) : 0;
    }

    public static getNumCycleByGroup(group: ProtocolQTestEssayGroup[], translate: TranslateService): string {
        let totalCycles = 0;
        let cycleText = '';
        for (let index = 0; index < group?.length; index++) {
            const element = group[index].essayValues[0].fields?.find(field => field.idField === FieldEnum.NUM_CYCLES); 

            if (element) { 
                totalCycles += Number(element.value);
            }
        }

        if (totalCycles) {
            const res = Number(totalCycles);
            if (res === 1) {
                cycleText = translate.instant('test.essay.cycle.singular', {cycle: totalCycles});
            } else {
                cycleText = translate.instant('test.essay.cycle.plural', {cycle: totalCycles});
            }
        } else {
            cycleText = translate.instant('test.contracted.items.elcycle', {cycle: totalCycles});
        }

        return cycleText;
    }

    public static getNumCycle(group: ProtocolQTestEssayGroup[]): number {
        let totalCycles = 0;
        for (let index = 0; index < group?.length; index++) {
            const element = group[index].essayValues[0].fields?.find(field => field.idField === FieldEnum.NUM_CYCLES); 

            if (element) { 
                totalCycles += Number(element.value);
            }
        }
        return totalCycles;
    }

    public static getEquipmentName(prot: ProtocolQ,): string {
        return prot.equipments?.[0]?.equipment;
    }

    public static getEquipmentNameComplete(prot: ProtocolQ,translate: TranslateService, isDelDela: boolean, isUnUna: boolean): string {
        const normalizedName = prot.equipments?.[0]?.equipment;
        if (normalizedName.endsWith('a')) {
            let translated;
            if (isDelDela) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.deLa');
            } else if (isUnUna) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.una');
            } else {
                translated = translate.instant('test.vacuumDistribution.contracted.items.la');
            }
            return `${translated} ${normalizedName}`;
        } else if (normalizedName.endsWith('o')) {
            let translated;
            if (isDelDela) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.del');
            } else if (isUnUna) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.un');
            } else {
                translated = translate.instant('test.vacuumDistribution.contracted.items.el');
            }
            return `${translated} ${normalizedName}`;
        } else if (normalizedName.endsWith('én')) {
            let translated;
            if (isDelDela) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.del');
            } else if (isUnUna) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.un');
            } else {
                translated = translate.instant('test.vacuumDistribution.contracted.items.el');
            }
            return `${translated} ${normalizedName}`;
        } else if (normalizedName.endsWith('as')) {
            let translated;
            if (isDelDela) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.deLa');
            } else if (isUnUna) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.una');
            }  else {
                translated = translate.instant('test.vacuumDistribution.contracted.items.la');
            }
            return `${translated} ${normalizedName}`;
        } else if (normalizedName.endsWith('N2')) {
            let translated;
            if (isDelDela) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.del');
            } else if (isUnUna) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.un');
            } else {
                translated = translate.instant('test.vacuumDistribution.contracted.items.el');
            }
            return `${translated} ${normalizedName}`;
        } else {
            return "";
        }
    }

    public static getEquipmentType(prot: ProtocolQ, translate: TranslateService, isDelDela: boolean, isUnUna: boolean): string {
        if (!prot.equipments || prot.equipments.length === 0) {
            return "";
        }
    
        const equipment = prot.equipments[0];
        if (!equipment || !equipment.equipment) {
            return "";
        }

        let normalizedName = '';
        if (equipment.idType === EquipmentTypeQualificationEnum.EQUIPMENT_TYPE || equipment.idType === EquipmentTypeQualificationEnum.WASHER_TYPE || equipment.idType === EquipmentTypeQualificationEnum.TANK_N2 || equipment.idType === EquipmentTypeQualificationEnum.CENTRIFUGE || equipment.idType === EquipmentTypeQualificationEnum.EST_DRY_HEAT) {
            normalizedName = translate.instant(`equipmentType.equipment`).toLowerCase();
        } else {
            normalizedName = translate.instant(`equipmentType.${equipment.nameType}`).toLowerCase();
        }
    
        if (normalizedName.endsWith('a')) {
            let translated;
            if (isDelDela) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.deLa');
            } else if (isUnUna) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.una');
            } else {
                translated = translate.instant('test.vacuumDistribution.contracted.items.la');
            }
            return `${translated} ${normalizedName}`;
        } else if (normalizedName.endsWith('o')) {
            let translated;
            if (isDelDela) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.del');
            } else if (isUnUna) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.un');
            } else {
                translated = translate.instant('test.vacuumDistribution.contracted.items.el');
            }
            return `${translated} ${normalizedName}`;
        } else if (normalizedName.endsWith('én')) {
            let translated;
            if (isDelDela) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.del');
            } else if (isUnUna) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.un');
            } else {
                translated = translate.instant('test.vacuumDistribution.contracted.items.el');
            }
            return `${translated} ${normalizedName}`;
        } else if (normalizedName.endsWith('as')) {
            let translated;
            if (isDelDela) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.deLa');
            } else if (isUnUna) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.una');
            }  else {
                translated = translate.instant('test.vacuumDistribution.contracted.items.la');
            }
            return `${translated} ${normalizedName}`;
        } else if (normalizedName.endsWith('N2')) {
            let translated;
            if (isDelDela) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.del');
            } else if (isUnUna) {
                translated = translate.instant('test.vacuumDistribution.contracted.items.un');
            } else {
                translated = translate.instant('test.vacuumDistribution.contracted.items.el');
            }
            return `${translated} ${normalizedName}`;
        } else {
            return "";
        }
    }

    public static getEquipmentLocation(prot: ProtocolQ): string {
        if (!prot.equipments || prot.equipments.length === 0) {
            return "";
        }
    
        const equipment = prot.equipments[0];
        if (!equipment || !equipment.equipment) {
            return "";
        }

        return equipment.location;
    }

    public static getEquipmentInternalId(prot: ProtocolQ): string {
        if (!prot.equipments || prot.equipments.length === 0) {
            return "";
        }
    
        const equipment = prot.equipments[0];
        if (!equipment || !equipment.equipment) {
            return "";
        }

        return equipment.internalId;
    }

    public static getEquipmentModel(prot: ProtocolQ): string {
        if (!prot.equipments || prot.equipments.length === 0) {
            return "";
        }
    
        const equipment = prot.equipments[0];
        if (!equipment || !equipment.equipment) {
            return "";
        }

        return equipment.model;
    }

    public static getEquipmenMaker(prot: ProtocolQ): string {
        if (!prot.equipments || prot.equipments.length === 0) {
            return "";
        }
    
        const equipment = prot.equipments[0];
        if (!equipment || !equipment.equipment) {
            return "";
        }

        return equipment.maker;
    }

    public static getEquipmenSerialNumber(prot: ProtocolQ): string {
        if (!prot.equipments || prot.equipments.length === 0) {
            return "";
        }
    
        const equipment = prot.equipments[0];
        if (!equipment || !equipment.equipment) {
            return "";
        }

        return equipment.serialNumber;
    }

    public static isLiofilizador(prot: ProtocolQ,): boolean {
        return false;
    }

    public static getFreqMinSecs(value: string, translate: TranslateService): string {
        if (!value) {
            return '';
        }
    
        const d = Number(value);
        const rIn = Math.floor(d);
    
        if (d === 0) {
            return 'X';
        }
    
        const timeParts: string[] = [];
    
        if (rIn > 0) {
            let minutesMessage = `${rIn} ${translate.instant('generic.time.minute')}`;
            if (rIn > 1) {
                minutesMessage += 's';
            }
            timeParts.push(minutesMessage);
        }
    
        const seconds = Math.round((d - rIn) * 60);
        if (seconds > 0) {
            let secondsMessage = `${seconds} ${translate.instant('generic.time.second')}`;
            if (seconds > 1) {
                secondsMessage += 's';
            }
            timeParts.push(secondsMessage);
        }
        return timeParts.length > 0 ? timeParts.join(', ') : '';
    }

    public static getNumberSensorTemperatureHumd(group: ProtocolQTestEssayGroup): number {
        const totalSensors = group.essayValues.filter(eve => eve.idVariable === VariableTypeEnum.TEMPERATURE || eve.idVariable === VariableTypeEnum.LETHALITY);
        return totalSensors != null ? totalSensors[0].sensors.length : 0;
    }

    public static getNumberSensorCo2(group: ProtocolQTestEssayGroup): number {
        const totalSensors = group.essayValues.filter(eve => eve.idVariable === VariableTypeEnum.CO2);
        return totalSensors != null ? totalSensors.length : 0;
    }

    public static getNumberSensorPressure(group: ProtocolQTestEssayGroup): number {
        const totalSensors = group.essayValues.filter(eve => eve.idVariable === VariableTypeEnum.PRESSURE);
        return totalSensors != null ? totalSensors.length : 0;
    }

    public static getNumberSensor(group: ProtocolQTestEssayGroup): number {
        return group.essayValues.reduce((sum, ev) => sum + ev.sensors.length, 0);
    }

    public static getEssayType(group: ProtocolQTestEssayGroup, translate: TranslateService): string {
        return (translate.instant('essayType.' + group.translationType) as string).toLowerCase();
    }

    public static getCycleText(group: ProtocolQTestEssayGroup, translate: TranslateService): string {
        const cycles = this.getCycleNumber(group);

        if (cycles > 1) {
            return translate.instant('test.vacuumDistribution.contracted.items.loscycle');
        } else {
            return translate.instant('test.vacuumDistribution.contracted.items.elcycle');
        }

    }

    public static getCycleNumber(essay: ProtocolQTestEssayGroup) {
        const frequency = essay.essayValues?.[0]?.fields?.find(field => field.idField === FieldEnum.FREQUENCY);

        if (frequency?.value) {
            return Number(frequency.value)
        } else {
            return 0;
        }
    }

    public static getCriteriaFromEssayComplete(essay: ProtocolQTestEssayGroup, translate: TranslateService, cycle: number): string[] {
		let res = [];
        if (!essay || !essay.essayValues) {
			return null;
		}

        for (let index = 0; index < essay.essayValues.length; index++) {
            const element = essay.essayValues[index];
            res.push(EssayUtils.getCriteriasToShowQualificatesWithSpace(element, essay.essayValues[0], essay.idType, translate, cycle));
        }

		return res;
	}

    public static existsOpenDoor(essay: ProtocolQTestEssayGroup): boolean {
        return essay.idType === EssayTypeEnum.ENSAYO_APERTURA_DE_PUERTA;
    }

    public static frequencyEssay(essay: ProtocolQTestEssayGroup, translate: TranslateService): string {
        let frequency = '';
        const highestFrequency = essay.essayValues?.[0]?.fields?.filter(field => field.idField === FieldEnum.FREQUENCY).map(field => field.value).reduce((max, current) => Math.max(max, current), 0);
        if (highestFrequency) {
            frequency += Number(highestFrequency);
        }
        return this.getFreqMinSecs(frequency.toString(), translate);
    }

    public static existsCutElectric(essay: ProtocolQTestEssayGroup): boolean {
        return essay.idType === EssayTypeEnum.ENSAYO_CORTE_ELECTRICO;    
    }

    public static getCycleFields(essay: ProtocolQTestEssayGroup) {
        let totalCycles = 1;
        if (essay.essayValues != null && essay.essayValues.length > 0) {
            const field = essay.essayValues[0].fields.find(field => field.idField === FieldEnum.NUM_CYCLES);
            if (field?.value != null) {
                totalCycles = Number(field.value);
            }
        }
        return totalCycles;
        
    }

    public static getValueDayHourMin(value: string | string[], translate: TranslateService): string {
		let res = '';

		if (value != null) {
            let values = [];
            if (typeof value === 'string') {
                values = value.split(";");
            } else if (Array.isArray(value)) {
                if (Array.isArray(value[0])) {
                    values = value[0];
                } else if (value.length === 1 && typeof value[0] === 'string') {
                    values = value[0].split(";");
                } else {
                    values = value;
                }
            }

			let vals = [];

            if (values?.length > 0) {
                let val = values[0];
                if (val && val !== "0") {
                    let str = val + " " + translate.instant('generic.time.day');
                    if (val !== "1") {
                        str = str.concat("s");
                    }
                    vals.push(str);
                }
                
                val = values[1];
                if (val && val !== "0") {
                    let str = val + " " + translate.instant('generic.time.hour');
                    if (val !== "1") {
                        str = str.concat("s");
                    }
                    vals.push(str);
                }
    
                val = values[2];
                if (val && val !== "0") {
                    let str = val + " " + translate.instant('generic.time.minute');
                    if (val !== "1") {
                        str = str.concat("s");
                    }
                    vals.push(str);
                }
            }

            if (vals) {
                res = vals.join(", ");
            }
            
		}

		return res;
    }

    public static differenceInSensory(protocol: ProtocolQTest): boolean {
        let allValuesAreEqual: boolean = false;

        if (protocol.protocolQTestEssayGroup) {
            const baseLength = protocol.protocolQTestEssayGroup[0]?.essayValues[0]?.sensors?.length ?? 0;

            return protocol.protocolQTestEssayGroup.some(group =>
                group.essayValues?.some(ev => (ev.sensors?.length ?? 0) !== baseLength)
            );
        }
        return false;
    }

    public static getProgramaByEssay(essay: ProtocolQTestEssay): string {
        if (essay != null) {
            const field = essay?.fields?.find(field => field.idField === FieldEnum.PROGRAM);
            if (field?.value != null) {
                return field.value;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    public static getFrequencyByEssay(essay: ProtocolQTestEssay): string {
        if (essay != null) {
            const field = essay[0].fields?.find(field => field.idField === FieldEnum.FREQUENCY);
            if (field?.value != null) {
                return field.value;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    public static differenceInFrequency(protocol: ProtocolQTest): boolean {
        const group = protocol.protocolQTestEssayGroup;
        return group?.every(({ essayValues }) =>
            essayValues.every(({ fields }) => {
                const freqFields = fields?.filter(f => f.idField === FieldEnum.FREQUENCY) || [];
                const [firstValue, ...rest] = freqFields.map(f => f.value);
                return rest.every(val => val !== firstValue);
            })
        ) ?? false;
    }

    public static differenceInCycle(protocol: ProtocolQTest): boolean {
        const group = protocol.protocolQTestEssayGroup;
        let allValuesAreEqual: boolean = false;
        for (let i = 0; i < group?.length; i++) {
            const element = group[i];
            for (let j = 0; j < element.essayValues.length; j++) {
                const essayValues = element.essayValues[j];
                const cyclesField = essayValues.fields?.find(field => field.idField === FieldEnum.NUM_CYCLES);

                if (cyclesField) {
                    const firstFieldValue = cyclesField.value;

                    if (!essayValues.fields.every(field => 
                        field.idField !== FieldEnum.NUM_CYCLES || field.value === firstFieldValue
                    )) {
                        allValuesAreEqual = false;
                        break;
                    }
                }
        
            }
            
            if (!allValuesAreEqual) {
                break;
            }
        }
    
        return allValuesAreEqual;
    }

    public static getDifferenceFrequency(group: ProtocolQTestEssayGroup, translate: TranslateService): string {
        let res: string = '';

        const values = group.essayValues;
        const existsTmpHum = this.existsTempHumdByEssay(values);
        const existsTmpLeth = this.existsTempLethalityByEssay(values);
        const essayName = (translate.instant(`essayType.${group.translationType}`) as string).toLowerCase();
        let isCompleteHumTemp: boolean = false;
        let isCompleteHumLeth: boolean = false;
        for (let index = 0; index < values?.length; index++) {
            const element = values[index];

            let resFrequency = element.fields?.find(e => e.idField === FieldEnum.FREQUENCY)?.value;

            if (resFrequency) {
                resFrequency = this.getFreqMinSecs(resFrequency, translate);


                if ((element.idVariable === VariableTypeEnum.TEMPERATURE || element.idVariable === VariableTypeEnum.HUMIDITY) && existsTmpHum) {
                    if (!isCompleteHumTemp) {
                        res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.frequency.item', {variable: this.getVariablesTempHumdByEssay(values, translate, true), time: resFrequency, essayName: essayName })}</p>`;
                        isCompleteHumTemp = true;
                    }
                } else if ((element.idVariable === VariableTypeEnum.TEMPERATURE ||  element.idVariable === VariableTypeEnum.LETHALITY) && existsTmpLeth) {
                    if(!isCompleteHumLeth) {
                        res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.frequency.item', {variable: this.getVariablesTempLetalidadByEssay(values, translate, true), time: resFrequency, essayName: essayName })}</p>`;
                        isCompleteHumLeth = true;
                    }
                } else {
                    let variable = '';
                    if (element.idVariable === VariableTypeEnum.CO2) {
                        variable = translate.instant(`variable.${element.translationVariable}`);
                    } else {
                        variable = (translate.instant(`variable.${element.translationVariable}`) as string).toLowerCase();
                    }
                    res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.frequency.item', {variable: variable, time: resFrequency, essayName: essayName })}</p>`;
                }
            }
        }
        return res;
    }

    public static getDifferenceSensor(group: ProtocolQTestEssayGroup, translate: TranslateService): string {
        let res: string = '';
        const values = group.essayValues;
        const existsTmpHum = this.existsTempHumdByEssay(values);
        const existsTmpLeth = this.existsTempLethalityByEssay(values);
        const essayName = translate.instant(`essayType.${group.translationType}`) as string;
        let isCompleteHumTemp: boolean = false;
        let isCompleteHumLeth: boolean = false;
        for (let index = 0; index < values?.length; index++) {
            const element = values[index];
            
            const program = this.getProgramaByEssay(element);
            let programText = '';
            if (StringUtils.isNotEmpty(program)) {
                programText += ` ${translate.instant('test.vacuumDistribution.methodology.secondSubtitle.differenceSensor.item.program', {programText: program.toLowerCase() })}`
            } else if (group.title) {
                programText += ` (${group.title.toLowerCase()})`
            }
            const probeNumber = element.sensors != null ? element.sensors.length : 0;

            if ((element.idVariable === VariableTypeEnum.TEMPERATURE || element.idVariable === VariableTypeEnum.HUMIDITY) && existsTmpHum) {
                if (!isCompleteHumTemp) {
                    res += `<p>${translate.instant('test.vacuumDistribution.methodology.secondSubtitle.differenceSensor.item', {probeNumber: probeNumber, variable: this.getVariablesTempHumdByEssay(values, translate, true)?.toLowerCase(), nameEssay: essayName?.toLowerCase(), programText: programText })}</p>`;
                    isCompleteHumTemp = true;
                }
            } else if ((element.idVariable === VariableTypeEnum.TEMPERATURE ||  element.idVariable === VariableTypeEnum.LETHALITY) && existsTmpLeth) {
                if(!isCompleteHumLeth) {
                    res += `<p>${translate.instant('test.vacuumDistribution.methodology.secondSubtitle.differenceSensor.item', {probeNumber: probeNumber, variable: this.getVariablesTempLetalidadByEssay(values, translate, true)?.toLowerCase(), nameEssay: essayName?.toLowerCase(), programText: programText })}</p>`;
                    isCompleteHumLeth = true;
                }
            } else {
                let variable = ''
                if (element.idVariable === VariableTypeEnum.CO2) {
                    variable = translate.instant(`variable.${element.translationVariable}`);
                } else {
                    variable = (translate.instant(`variable.${element.translationVariable}`) as string).toLowerCase();
                }
                res += `<p>${translate.instant('test.vacuumDistribution.methodology.secondSubtitle.differenceSensor.item', {probeNumber: probeNumber, variable, nameEssay: essayName?.toLowerCase(), programText: programText })}</p>`;
            }
        }
        return res;
    }

    public static getDifferenceCycle(group: ProtocolQTestEssayGroup, translate: TranslateService): string {
        let res: string = '';
        const values = group.essayValues;
        let cycleEssayText = this.getNumCycleTextIndividual(group, translate);
        const essayName = (translate.instant(`essayType.${group.translationType}`) as string).toLowerCase();
        const existsTmpHum = this.existsTempHumdByEssay(values);
        const existsTmpLeth = this.existsTempLethalityByEssay(values);
        let addBreak = false;
        let isCompleteHumTemp: boolean = false;
        if (values && values[0]) {
            addBreak = false;
            const element = values[0];

            const program = this.getProgramaByEssay(element);
            let programText = '';
            if (StringUtils.isNotEmpty(program)) {
                programText += ` ${translate.instant('test.vacuumDistribution.methodology.secondSubtitle.cycle.item.program', {programText: program.toLowerCase() })}`;
            } else if (group.title) {
                programText += ` (${group.title.toLowerCase()})`
            }

            if (existsTmpHum) {
                if (!isCompleteHumTemp) {
                    res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.cycle.item', {cycleText: cycleEssayText, essayName: essayName, programText: programText })}</p>`;
                    isCompleteHumTemp = true;
                    addBreak = true;
                }
            } else {
                res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.cycle.item', {cycleText: cycleEssayText, essayName: essayName, programText: programText })}</p>`;
                addBreak = true;
            }

            /*if (addBreak) {
                res += `<span><br></span>`;
            }*/
        }
        return res;
    }

    public static getNumberSensorTemperatureHumdByEssay(essayValues: ProtocolQTestEssay[]): number {
        const totalSensors = essayValues.filter(eve => eve.idVariable === VariableTypeEnum.TEMPERATURE || eve.idVariable === VariableTypeEnum.LETHALITY);
        return totalSensors != null ? totalSensors[0].sensors.length : 0;
    }

    public static getNumberSensorCo2ByEssay(essayValues: ProtocolQTestEssay[]): number {
        const totalSensors = essayValues.filter(eve => eve.idVariable === VariableTypeEnum.CO2);
        return totalSensors != null ? totalSensors.length : 0;
    }

    public static existsPressureProbeByEssay(essayValues: ProtocolQTestEssay[]): boolean {
        return essayValues.some(element => element.idVariable === VariableTypeEnum.PRESSURE);
    }

    public static existsTempHumdByEssay(essayValues: ProtocolQTestEssay[]): boolean {
        return essayValues.some(eve => eve.idVariable === VariableTypeEnum.TEMPERATURE) && essayValues.some(eve => eve.idVariable === VariableTypeEnum.HUMIDITY);
    }

    public static existsTempLethalityByEssay(essayValues: ProtocolQTestEssay[]): boolean {
        return essayValues.some(eve => eve.idVariable === VariableTypeEnum.TEMPERATURE) && essayValues.some(eve => eve.idVariable === VariableTypeEnum.LETHALITY);
    }

    public static existsPressureCo2ByEssay(essayValues: ProtocolQTestEssay[]): boolean {
        return essayValues.some(element => element.idVariable === VariableTypeEnum.CO2);
    }

    public static getVariablesTempHumdByEssay(essayValues: ProtocolQTestEssay[], translate: TranslateService, isTempHum: boolean): string {
        let translatedValues: string[] = [];
    
        translatedValues = essayValues
            .filter(i => i.idVariable === VariableTypeEnum.TEMPERATURE || i.idVariable === VariableTypeEnum.HUMIDITY)
            .map(i => translate.instant('variable.' + i.translationVariable));

        translatedValues = Array.from(new Set(translatedValues));
    
        translatedValues = translatedValues.map(e => e.toLowerCase());
    
        if (translatedValues.length === 1) {
            return translatedValues[0];
        }
    
        if (translatedValues.length === 2) {
            return translatedValues.join(' y ');
        }
        const lastElement = translatedValues.pop();
        return `${translatedValues.join(', ')} y ${lastElement}`;
    }

    public static getVariablesTempLetalidadByEssay(essayValues: ProtocolQTestEssay[], translate: TranslateService, isTempHum: boolean): string {
        let translatedValues: string[] = [];
    
        translatedValues = essayValues
            .filter(i => i.idVariable === VariableTypeEnum.TEMPERATURE || i.idVariable === VariableTypeEnum.LETHALITY)
            .map(i => translate.instant('variable.' + i.translationVariable));

        translatedValues = Array.from(new Set(translatedValues));
    
        translatedValues = translatedValues.map(e => e.toLowerCase());
    
        if (translatedValues.length === 1) {
            return translatedValues[0];
        }
    
        if (translatedValues.length === 2) {
            return translatedValues.join(' y ');
        }
        const lastElement = translatedValues.pop();
        return `${translatedValues.join(', ')} y ${lastElement}`;
    }

    public static getVariablesByEssay(essayValues: ProtocolQTestEssay[], translate: TranslateService, isTempHum: boolean): string {
        let translatedValues: string[] = [];
    
        translatedValues = essayValues.map(i => {
            if (i.idVariable === VariableTypeEnum.CO2) {
                return translate.instant('variableSub.' + i.translationVariable)
            } else {
                return (translate.instant('variable.' + i.translationVariable) as string).toLowerCase();
            }
        });

        translatedValues = Array.from(new Set(translatedValues));
        
        if (translatedValues.length === 1) {
            return translatedValues[0];
        }
    
        if (translatedValues.length === 2) {
            return translatedValues.join(' y ');
        }
        const lastElement = translatedValues.pop();
        return `${translatedValues.join(', ')} y ${lastElement}`;
    }

    public static getTotalCycleNumber(test: ProtocolQTestEssayGroup[]): number {
        for (let index = 0; index < test.length; index++) {
            const element = test[index];
            const frequency = element.essayValues[0]?.fields.find(field => field.idField === FieldEnum.FREQUENCY);

            if (frequency._value) {
                return Number(frequency._value)
            } else {
                return 0;
            } 
        }
       
    }

    public static getTotalEssayText(groups: ProtocolQTestEssayGroup[], translate: TranslateService): string {
        let totalEssay = 0;
        let allowedEssay = [];
        let resText = '';

        for (let index = 0; index < groups.length; index++) {
            const element = groups[index];
            
            if (!allowedEssay.includes(element.uuid)) {
                totalEssay += 1;
            }
        }

        if (totalEssay > 1) {
            resText = translate.instant('test.essay.losessay')
        } else {
            resText = translate.instant('test.essay.elessay')
        }
        return resText;
    }

    public static getTotalEssayTextWithCount(groups: ProtocolQTestEssayGroup[], translate: TranslateService): string {
        let totalEssay = 0;
        let allowedEssay = [];
        let resText = '';

        for (let index = 0; index < groups.length; index++) {
            const element = groups[index];
            
            if (!allowedEssay.includes(element.uuid)) {
                totalEssay += 1;
            }
        }

        if (totalEssay > 1) {
            resText = totalEssay + " " + (translate.instant('test.essay.plural') as string).toLowerCase();
        } else {
            resText = translate.instant('test.vacuumDistribution.contracted.items.un') + " " + (translate.instant('test.essay.singular') as string).toLowerCase();
        }
        return resText;
    }

    public static getTotalEssay(groups: ProtocolQTestEssayGroup[]): number {
        let totalEssay = 0;
        let allowedEssay = [];

        for (let index = 0; index < groups.length; index++) {
            const element = groups[index];
            
            if (!allowedEssay.includes(element.uuid)) {
                totalEssay += 1;
            }
        }
        return totalEssay;
    }

    public static getDescriptionLoadWithTitle(test: ProtocolQTestEssayGroup[], translate: TranslateService): string {
        let res = '';
        for (let index = 0; index < test.length; index++) {
            const element = test[index];
            if (element.title) {
                res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.load.item', {essayType: translate.instant('essayType.'+element.translationType), titleEssay: ' ('+element.title+')', descriptionLoad: element.load})}</p>`;
            } else {
                res += `<p>${translate.instant('test.loadDistribution.methodology.secondSubtitle.load.item', {essayType: translate.instant('essayType.'+element.translationType), titleEssay: '', descriptionLoad: element.load})}</p>`;
            }
        }

        return res;
       
    }

    public static existsOutsideProbe(essay: ProtocolQTestEssayGroup): boolean {
        return essay.essayValues.some(element => {
           return element.sensors.some(s => s.outer);
        });
    }

    public static getVariableOutsideProbe(essay: ProtocolQTestEssayGroup, translante: TranslateService): string {
        let variable = [];
        for (let index = 0; index < essay.essayValues.length; index++) {
            const element = essay.essayValues[index];
            
            for (let indexSensor = 0; indexSensor < element.sensors.length; indexSensor++) {
                const sensor = element.sensors[indexSensor];

                if (sensor.outer && !variable.includes(translante.instant(`variable.${element.translationVariable}`))) {
                    if (element.idVariable === VariableTypeEnum.CO2) {
                        variable.push(translante.instant(`variableSub.${element.translationVariable}`) as string);
                    } else {
                        const resVariable = translante.instant(`variable.${element.translationVariable}`) as string;
                        variable.push(resVariable.toLowerCase());
                    }
                }
            }
        }

        return variable.join(', ');
    }

    public static getLethalityAccumulated(group: ProtocolQTestEssayGroup[]): boolean{
        if (group) {
            return group.some(e => e.essayValues && e.essayValues[0]?.fields.some(field => field.idField === FieldEnum.LETHALITY_F0 && field.value === 'accumulated'));
        } else {
            return false;
        }
    }

    public static getTotalOpenDoorText(groups: ProtocolQTestEssayGroup[], equipment: string, translate: TranslateService): string {
        let totalEssay = 0;
        let allowedEssay = [];
        let resText = '';

        let resGroups = groups.filter(e => e.idType === EssayTypeEnum.ENSAYO_APERTURA_DE_PUERTA);
        for (let index = 0; index < resGroups.length; index++) {
            const element = groups[index];
            
            if (!allowedEssay.includes(element.uuid)) {
                totalEssay += 1;
            }
        }

        if (totalEssay === 1) {
            resText = translate.instant('test.essay.openDoor.singular');
        } else if (totalEssay > 1) {
            resText = translate.instant('test.essay.openDoor.plural', {count: totalEssay});
        } else {
            return null;
        }

        let openDoorTime = this.getTimeExpositionOpenDoor(groups, translate);

        return translate.instant('test.methodology.time.openDoor.thirdSubtitle.text', {countEssay: resText, equipment: equipment, duration: openDoorTime})
    }

    public static getTotalCutElectricText(groups: ProtocolQTestEssayGroup[], translate: TranslateService): string {
        let totalEssay = 0;
        let allowedEssay = [];
        let resText = '';

        let resGroups = groups.filter(e => e.idType === EssayTypeEnum.ENSAYO_CORTE_ELECTRICO);
        for (let index = 0; index < resGroups.length; index++) {
            const element = groups[index];
            
            if (!allowedEssay.includes(element.uuid)) {
                totalEssay += 1;
            }
        }

        if (totalEssay === 1) {
            resText = translate.instant('test.essay.cutElectric.singular');
        } else if (totalEssay > 1) {
            resText = translate.instant('test.essay.cutElectric.plural', {count: totalEssay});
        } else {
            return null;
        }

        let openDoorTime = this.getTimeExpositionCutElectric(groups, translate);

        return translate.instant('test.methodology.time.cutElectric.thirdSubtitle.text', {countEssay: resText, duration: openDoorTime})
    }

    public static getTimeExpositionOpenDoor(groups: ProtocolQTestEssayGroup[], translate: TranslateService): string {
        let highestFrequency: Field;
        let res = [];
        let resTime = [];
        let essayGroup = groups.filter(e => e.idType === EssayTypeEnum.ENSAYO_APERTURA_DE_PUERTA);
        for (let index = 0; index < essayGroup?.length; index++) {
            const element = essayGroup[index];

            highestFrequency = element.essayValues?.[0]?.fields.find(field => field.idField === FieldEnum.REHEARSAL_TIME);
            if (highestFrequency) {
                res.push(highestFrequency.value)
            }
        }

        for (let index = 0; index < res.length; index++) {
            const element = res[index];
            
            resTime.push(this.getValueDayHourMin(element, translate));
        }

        if (resTime.length === 1) {
            return resTime[0];
        }
    
        if (resTime.length === 2) {
            return resTime.join(` ${translate.instant('test.essay.y')} `);
        }
        const lastElement = resTime.pop();
        return `${resTime.join(', ')} ${translate.instant('test.essay.y')} ${lastElement}`; 
    }

    public static getTimeExpositionCutElectric(groups: ProtocolQTestEssayGroup[], translate: TranslateService): string {
        let highestFrequency: Field;
        let res = [];
        let resTime = [];
        let essayGroup = groups.filter(e => e.idType === EssayTypeEnum.ENSAYO_CORTE_ELECTRICO);
        for (let index = 0; index < essayGroup?.length; index++) {
            const element = essayGroup[index];

            highestFrequency = element.essayValues?.[0]?.fields.find(field => field.idField === FieldEnum.REHEARSAL_TIME);
            if (highestFrequency) {
                res.push(highestFrequency.value)
            }
        }

        for (let index = 0; index < res.length; index++) {
            const element = res[index];
            
            resTime.push(this.getValueDayHourMin(element, translate));
        }

        if (resTime.length === 1) {
            return resTime[0];
        }
    
        if (resTime.length === 2) {
            return resTime.join(` ${translate.instant('test.essay.y')} `);
        }
        const lastElement = resTime.pop();
        return `${resTime.join(', ')} ${translate.instant('test.essay.y')} ${lastElement}`; 
    }

    public static getTotalOpenDoorCutElectric(groups: ProtocolQTestEssayGroup[], translate: TranslateService): string {
        let totalEssayOpen = 0;
        let totalEssayCut = 0;

        let allowedEssay = [];
        let resTextOpenDoor = '';
        let resTextCutElectric = '';

        let resGroupOpenDoor = groups.filter(e => e.idType === EssayTypeEnum.ENSAYO_APERTURA_DE_PUERTA);
        for (let index = 0; index < resGroupOpenDoor.length; index++) {
            const element = resGroupOpenDoor[index];
            
            if (!allowedEssay.includes(element.uuid)) {
                totalEssayOpen += 1;
            }
        }

        let resGroupCutElectric = groups.filter(e => e.idType === EssayTypeEnum.ENSAYO_CORTE_ELECTRICO);
        for (let index = 0; index < resGroupCutElectric.length; index++) {
            const element = resGroupCutElectric[index];
            
            if (!allowedEssay.includes(element.uuid)) {
                totalEssayCut += 1;
            }
        }

        if (totalEssayOpen === 1) {
            resTextOpenDoor = translate.instant('test.essay.openDoor.la');
        } else if (totalEssayOpen > 1) {
            resTextOpenDoor = translate.instant('test.essay.openDoor.las', {count: totalEssayOpen});
        } else {
            resTextOpenDoor = null;
        }


        if (totalEssayCut === 1) {
            resTextCutElectric = translate.instant('test.essay.cutElectric.el');
        } else if (totalEssayCut > 1) {
            resTextCutElectric = translate.instant('test.essay.cutElectric.los', {count: totalEssayCut});
        } else {
            resTextCutElectric = null;
        }

        if (resTextOpenDoor && resTextCutElectric) {
            return `${resTextOpenDoor} ${translate.instant('test.essay.y')} ${resTextCutElectric}`;
        } else if (resTextOpenDoor && !resTextCutElectric) {
            return `${resTextOpenDoor}`;
        } else if (!resTextOpenDoor && resTextCutElectric) {
            return `${resTextCutElectric}`;
        }
    }

    public static getTotalCutElectric(groups: ProtocolQTestEssayGroup[]): number {
        let totalEssay = 0;
        let allowedEssay = [];
        let resText = '';

        let resGroups = groups.filter(e => e.idType === EssayTypeEnum.ENSAYO_CORTE_ELECTRICO);
        for (let index = 0; index < resGroups.length; index++) {
            const element = groups[index];
            
            if (!allowedEssay.includes(element.uuid)) {
                totalEssay += 1;
            }
        }
        return totalEssay;
    }

    public static remplaceVariableInText(protocol: ProtocolQ, text: string, translate: TranslateService): string {
		const client = text.includes('[CLIENTE]');
		if (client) {
			if (protocol.nameClient) {
				text = text.replace(/\[CLIENTE\]/g, protocol.nameClient);
			} else {
				text = text.replace(/\[CLIENTE\]/g, 'XXXX');
			}
		}

		const qType = text.includes('[QUALIFICATION_TYPE]');
		if (qType) {
			if (protocol.testPerformed) {
				text = text.replace(/\[QUALIFICATION_TYPE\]/g, protocol.testPerformed?.map(e => translate.instant('testQualification.variable.' + e)).join('/'));
			} else {
				text = text.replace(/\[QUALIFICATION_TYPE\]/g, 'XXXX');
			}
		}

		const qTypeDescription = text.includes('[QUALIFICATION_TYPE_DESCRIPTION]');
		if (qTypeDescription) {
			if (protocol.testPerformed) {
				const resDes = protocol.testPerformed?.map(e => translate.instant('testQualification.variable.description.' + e));
				text = text.replace(/\[QUALIFICATION_TYPE_DESCRIPTION\]/g, StringUtils.concatenateText(resDes, translate));
			} else {
				text = text.replace(/\[QUALIFICATION_TYPE_DESCRIPTION\]/g, 'XXXX');
			}
		}
	
		const equipmentEl = text.includes('[EQUIPMENT_EL]');
		if (equipmentEl) {
			text = text.replace('[EQUIPMENT_EL]', this.getEquipmentNameComplete(protocol, translate, false, false));
		}

        const equipmentDel = text.includes('[EQUIPMENT_DEL]');
		if (equipmentDel) {
			text = text.replace('[EQUIPMENT_DEL]', this.getEquipmentNameComplete(protocol, translate, true, false));
		}

		return text;
	}

    public static getStrategyQualificates(protocol: ProtocolQ, clientName: string, translate: TranslateService, image: string) {
        let qualified = '';
        let equipmentWithDelDeLa = this.getEquipmentType(protocol, translate, true, false);
        if (!equipmentWithDelDeLa.endsWith('a')) {
            qualified =  translate.instant('protocol.background.qualified.masc');
        } else {
            qualified = translate.instant('protocol.background.qualified.fem');
        }

        const qualificationText =  StringUtils.concatenateText(protocol.testPerformed?.map(e => translate.instant('testQualification.variable.description.' + e)), translate);
        const qualification = protocol.testPerformed?.map(e => translate.instant('testQualification.variable.' + e)).join('/')
        
        let resText = `<span>${translate.instant('protocolEdit.qualification.strategy.text', {client: clientName, equipment: equipmentWithDelDeLa, qualificationText: qualificationText, qualification: "("+qualification+")", qualified: qualified})}</span>`;
        resText += `<p><br></p>`;
        resText += `<p class="ql-align-center"><strong>${translate.instant('protocolEdit.qualification.strategy.text.title', {qualification: qualification})}</strong></p>`;
        resText += `<span><br></span>`;
        resText += `<p class="ql-align-center">${image}</p>`;
        resText += `<p><br></p>`;
        resText += `${translate.instant('protocolEdit.qualification.strategy.text.content', {client: clientName, equipment: equipmentWithDelDeLa, qualificationText: qualificationText, qualification: qualification, qualified: qualified})}`;

        return resText;
    }

    static getEquipmentByField(field: string, equipment: EquipmentQualificates[]): string {
        const fieldMap = {
            'Equipo': 'equipment',
            'Modelo': 'model',
            'Marca': 'maker',
            'Número de serie': 'serialNumber',
            'Identificativo': 'internalId',
            'Ubicación': 'location',
            'Modelo rotor': 'rotorModel',
            'Número de serie rotor': 'rotorSerial'
        };

        const fieldKey = fieldMap[field];
        if (!fieldKey) return null;

        const res = equipment.find(e => e[fieldKey]);
        return res ? res[fieldKey] : null;
    }

    public static getVariablesJoinWithoutLethality(essay: ProtocolQTest, translate: TranslateService, isTempHum: boolean): string {
        let translatedValues: string[] = [];
    
        for (let index = 0; index < essay.protocolQTestEssayGroup?.length; index++) {
            const element = essay.protocolQTestEssayGroup[index];
            if (isTempHum) {
                translatedValues = element.essayValues
                    .filter(i => i.idVariable === VariableTypeEnum.TEMPERATURE || i.idVariable === VariableTypeEnum.HUMIDITY)
                    .map(i => translate.instant('variable.' + i.translationVariable));
            } else {
                translatedValues = element.essayValues
                    .filter(e => e.idVariable !== VariableTypeEnum.LETHALITY).map(i => {
                        if (i.idVariable === VariableTypeEnum.CO2) {
                            return translate.instant('variableSub.' + i.translationVariable)
                        } else {
                            return (translate.instant('variable.' + i.translationVariable) as string).toLowerCase();
                        }
                    });
            }
        
            translatedValues = Array.from(new Set(translatedValues));
                
            if (translatedValues.length === 1) {
                return translatedValues[0];
            }
        
            if (translatedValues.length === 2) {
                return translatedValues.join(' y ');
            }
        }
        const lastElement = translatedValues.pop();
        return `${translatedValues.join(', ')} y ${lastElement}`;
    }

    public static hasBioindicador(group: ProtocolQTestEssayGroup[]): boolean {
        return  group?.some(g => g.essayValues?.some(value =>
              value.sensors?.some(sensor => sensor.hasBioindicator)
          )) ?? false;
    }

    public static hasBioindicadorIndivitual(group: ProtocolQTestEssayGroup): boolean {
        return  group.essayValues?.some(value =>
              value.sensors?.some(sensor => sensor.hasBioindicator)
          ) ?? false;
    }

    public static validateExpositionTime(desdeStr: string, hastaStr: string, timeExposition: string | any[]): boolean {
        let daysStr = '0', hoursStr = '0', minutesStr = '0';
        if (Array.isArray(timeExposition)) {
            if (timeExposition.length !== 3) {
                throw new Error('El array timeExposition debe contener exactamente 3 elementos: [días, horas, minutos]');
            }
            [daysStr, hoursStr, minutesStr] = timeExposition.map(part => part.trim());
        } else if (typeof timeExposition === 'string') {
            const parts = timeExposition.split(';').map(part => part.trim());
            if (parts.length !== 3) {
                throw new Error('El string timeExposition debe tener el formato "días;horas;minutos"');
            }
            [daysStr, hoursStr, minutesStr] = parts;
        } else {
            throw new Error('timeExposition debe ser un string o un array de strings');
        }
        
        const days = parseInt(daysStr, 10) || 0;
        const hours = parseInt(hoursStr, 10) || 0;
        const minutes = parseInt(minutesStr, 10) || 0;

        const from = DateTime.fromISO(desdeStr);
        const until = DateTime.fromISO(hastaStr);

        if (!from.isValid || !until.isValid) {
            throw new Error('Fechas inválidas');
        }

        const diff = until.diff(from, ['days', 'hours', 'minutes']);

        const requerid = Duration.fromObject({
            days: days,
            hours: hours,
            minutes: minutes,
        });

        return diff.as('milliseconds') === requerid.as('milliseconds');
    }

    public static getMaxNumberDeviation(codes: string[]): string | null {
        const numbers = codes
        ?.map(code => {
        const match = code?.match(/-(\d+(?:\.\d+)*)(?=\s|$)/);
        return match ? parseFloat(match[1]) : null;
        })
        .filter((num): num is number => num !== null);

        if (!numbers || numbers?.length === 0) return "1";

        const res = Math.max(...numbers) + 1; 
        
        return res < 10 ? '0' + res : res.toString();
    }
}
